import { ICommand } from './ICommand';
import { getNodeMutationManager } from './mutations/NodeMutationManager';
import type { TableCellKey } from '../../stores/tableCellStore';
import type { TableCellStyle, TableCellStyleMap } from '../../domains/diagram/models/table/tableCell.types';

interface ClearTableCellStylesCommandParams {
  nodeIds: string[];
  cellKeysByNodeId: Record<string, TableCellKey[]>;
}

type PreviousEntry = { cellKey: TableCellKey; prev: TableCellStyle | undefined };

export class ClearTableCellStylesCommand implements ICommand {
  id = `clear-table-cell-styles-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  name = 'Clear Table Cell Styles';
  timestamp = Date.now();

  private previousByNodeId: Record<string, PreviousEntry[]> = {};

  constructor(private params: ClearTableCellStylesCommandParams) {}

  async execute(): Promise<void> {
    const mutationManager = getNodeMutationManager();
    this.previousByNodeId = {};

    for (const nodeId of this.params.nodeIds) {
      const keys = this.params.cellKeysByNodeId[nodeId] ?? [];
      if (keys.length === 0) continue;
      const node = mutationManager.getNode(nodeId);
      if (!node) continue;
      const attrs = (node.attrs ?? {}) as Record<string, unknown>;
      const prevMap = (attrs.cellStyles as TableCellStyleMap | undefined) ?? {};
      const nextMap: TableCellStyleMap = { ...prevMap };

      const prevEntries: PreviousEntry[] = [];
      for (const k of keys) {
        prevEntries.push({ cellKey: k, prev: prevMap[k] });
        delete nextMap[k];
      }

      this.previousByNodeId[nodeId] = prevEntries;
      const changes = { attrs: { cellStyles: nextMap } } as const;
      mutationManager.applyNodeChange(nodeId, changes);
    }
  }

  async undo(): Promise<void> {
    const mutationManager = getNodeMutationManager();

    for (const [nodeId, prevEntries] of Object.entries(this.previousByNodeId)) {
      const node = mutationManager.getNode(nodeId);
      if (!node) continue;
      const attrs = (node.attrs ?? {}) as Record<string, unknown>;
      const curMap = (attrs.cellStyles as TableCellStyleMap | undefined) ?? {};
      const nextMap: TableCellStyleMap = { ...curMap };

      for (const entry of prevEntries) {
        if (entry.prev) {
          nextMap[entry.cellKey] = entry.prev;
        }
      }

      const changes = { attrs: { cellStyles: nextMap } } as const;
      mutationManager.applyNodeChange(nodeId, changes);
    }
  }

  async redo(): Promise<void> {
    await this.execute();
  }

  getMetadata(): Record<string, unknown> {
    return {
      nodeIds: this.params.nodeIds,
    };
  }
}


